import type { ReactNode } from "react";
import type { TaxonomyTerm } from "../api/types";

const technicalIdentifierPattern = "[A-Za-z][A-Za-z0-9]*(?:_[A-Za-z0-9]+)+";

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function humanizeIdentifier(id: string): string {
  const words = id.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function taxonomyTermValueMap(terms: TaxonomyTerm[]): Map<string, TaxonomyTerm> {
  return new Map(terms.flatMap((term) => [[term.id, term], [term.label, term]]));
}

function termPattern(terms: TaxonomyTerm[]): RegExp {
  const values = [...taxonomyTermValueMap(terms).keys()].sort((a, b) => b.length - a.length);
  const alternatives = values.length > 0
    ? `${values.map(escapeRegExp).join("|")}|${technicalIdentifierPattern}`
    : technicalIdentifierPattern;
  return new RegExp(`(?<![A-Za-z0-9_])(${alternatives})(?![A-Za-z0-9_])`, "g");
}

export function taxonomyTermMap(terms: TaxonomyTerm[]): Map<string, TaxonomyTerm> {
  return new Map(terms.map((term) => [term.id, term]));
}

export function termLabel(id: string, terms: TaxonomyTerm[]): string {
  return taxonomyTermMap(terms).get(id)?.label ?? humanizeIdentifier(id);
}

export function replaceTaxonomyTerms(text: string, terms: TaxonomyTerm[]): string {
  const byValue = taxonomyTermValueMap(terms);
  return text.replace(termPattern(terms), (value) => byValue.get(value)?.label ?? humanizeIdentifier(value));
}

export function TaxonomyText({
  text,
  terms,
}: {
  text: string;
  terms: TaxonomyTerm[];
}) {
  const byValue = taxonomyTermValueMap(terms);
  const pattern = termPattern(terms);
  const parts: ReactNode[] = [];
  let cursor = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > cursor) parts.push(text.slice(cursor, index));
    const term = byValue.get(match[0]);
    if (term) {
      parts.push(
        <span className="taxonomy-term" tabIndex={0} key={`${index}-${term.id}`}>
          {term.label}
          <span className="taxonomy-tooltip" role="tooltip">{term.description}</span>
        </span>,
      );
    } else {
      parts.push(
        <span className="taxonomy-fallback" key={`${index}-${match[0]}`}>
          {humanizeIdentifier(match[0])}
        </span>,
      );
    }
    cursor = index + match[0].length;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}
